<div class="site-shell">
    @if (!(isHandset$ | async)) {
    <aside class="side-rail">
        <div class="brand-mark" (click)="nav('home')">
            <img class="brand-mark__icon" src="favicon.ico" alt="Logo" />
            <div class="brand-mark__copy">
                <span class="eyebrow">Michael Mason</span>
                <span class="eyebrow">Solutions Architect</span>
            </div>
        </div>
        <nav class="rail-nav" aria-label="Primary">
            @for (section of sections; track section) {
            <button
                type="button"
                class="rail-nav__item"
                (click)="nav(section.anchor)"
            >
                <span
                    class="material-icons rail-nav__icon"
                    aria-hidden="true"
                    >{{ section.icon }}</span
                >
                <span>{{ section.label }}</span>
            </button>
            }
        </nav>
        <div class="rail-card">
            <p class="rail-card__label">Based in</p>
            <p class="rail-card__value">Raleigh, North Carolina</p>
            <p class="rail-card__meta">
                Full stack delivery, cloud infrastructure, and public-sector
                systems.
            </p>
        </div>
    </aside>
    }

    <div class="content-shell">
        @if (isHandset$ | async) {
        <header class="mobile-nav">
            <div class="mobile-nav__intro">
                <span class="eyebrow">Michael Mason</span>
                <span class="eyebrow">Solutions Architect</span>
            </div>
            <div class="mobile-nav__links">
                @for (section of sections; track section) {
                <button
                    type="button"
                    class="mobile-nav__button"
                    (click)="nav(section.anchor)"
                >
                    {{ section.label }}
                </button>
                }
            </div>
        </header>
        }

        <main class="page-content">
            <section class="hero" id="home">
                <div class="hero__copy">
                    <span class="eyebrow">Portfolio</span>
                    <h1>Cloud architecture for critical missions.</h1>
                    <p class="hero__lede">
                        AWS Solutions Architect supporting the Department of
                        Justice. Previously a software engineer at Amazon
                        building financial systems, and a consultant modernizing
                        federal IT at the Department of Homeland Security.
                    </p>
                    <div class="hero__actions">
                        <button
                            type="button"
                            class="hero__button hero__button--primary"
                            (click)="nav('experience')"
                        >
                            View Experience
                        </button>
                        <button
                            type="button"
                            class="hero__button"
                            (click)="nav('contact')"
                        >
                            Contact Me
                        </button>
                    </div>
                </div>

                <div class="hero__spotlight">
                    <div class="stat-card">
                        <span class="stat-card__value">AWS</span>
                        <span class="stat-card__label"
                            >Solutions Architect — Department of Justice</span
                        >
                    </div>
                    <div class="stat-card">
                        <span class="stat-card__value">SDE</span>
                        <span class="stat-card__label"
                            >Software Engineer — Amazon Financial Systems</span
                        >
                    </div>
                    <div class="stat-card">
                        <span class="stat-card__value">VT</span>
                        <span class="stat-card__label"
                            >Bachelor's and Master's in Industrial and Systems
                            Engineering</span
                        >
                    </div>
                </div>
            </section>

            <section class="section-grid section-grid--about" id="about">
                <div class="section-heading">
                    <span class="eyebrow">About</span>
                    <h2>Who is Michael?</h2>
                </div>

                <div class="feature-card feature-card--wide">
                    <p>
                        I am an AWS Solutions Architect based in Raleigh, North
                        Carolina, currently supporting the Department of Justice.
                        Before joining AWS, I was a Software Development Engineer
                        at Amazon building financial systems, and prior to that a
                        consultant modernizing IT infrastructure at the Department
                        of Homeland Security.
                    </p>
                    <p>
                        My academic background is in Industrial and Systems
                        Engineering. I hold both a Bachelor's and Master's
                        degree from Virginia Tech, along with an AWS Solutions
                        Architect Associate and AWS Developer Associate
                        certification.
                    </p>
                    <div class="logo-row">
                        <img
                            src="./../assets/dhs.png"
                            alt="Department of Homeland Security logo"
                        />
                        <img
                            src="./../assets/virginia-tech-hokies-logo-png-transparent.png"
                            alt="Virginia Tech logo"
                        />
                        <img
                            src="./../assets/aws-sa.png"
                            alt="AWS Solutions Architect badge"
                        />
                    </div>
                </div>

                <div class="feature-card media-card">
                    <img
                        src="./../assets/greece.JPG"
                        alt="Michael Mason outdoors"
                    />
                </div>

                <div class="feature-card">
                    <h3>Outside of work</h3>
                    <p>
                        In my free time I gravitate toward running, exercise,
                        audiobooks, and podcasts. Science fiction, dystopian
                        novels, financial commentary, and technology podcasts
                        tend to dominate the queue.
                    </p>
                </div>
            </section>

            <section class="section-grid" id="skills">
                <div class="section-heading">
                    <span class="eyebrow">Skills</span>
                    <h2>Full stack engineering with delivery breadth.</h2>
                </div>

                <div class="feature-card feature-card--wide">
                    <p>
                        I enjoy building cloud-native web applications end to
                        end, from Angular user interfaces to Java and Spring
                        Boot APIs to infrastructure on AWS. I am most effective
                        where product delivery, platform thinking, and DevOps
                        discipline all need to coexist.
                    </p>
                </div>

                <div class="feature-card icon-card">
                    <h3>Core stack</h3>
                    <div class="tech-grid">
                        <div class="tech-pill">
                            <img src="./../assets/angular.png" alt="Angular" />
                            <span>Angular</span>
                        </div>
                        <div class="tech-pill">
                            <img src="./../assets/java.png" alt="Java" />
                            <span>Java</span>
                        </div>
                        <div class="tech-pill">
                            <img src="./../assets/aws.png" alt="AWS" />
                            <span>AWS</span>
                        </div>
                        <div class="tech-pill">
                            <img
                                src="./../assets/postgresql.png"
                                alt="TypeScript"
                            />
                            <span>TypeScript</span>
                        </div>
                    </div>
                </div>

                <div class="feature-card">
                    <h3>How I work</h3>
                    <ul class="detail-list">
                        <li>Frontend development with Angular and TypeScript</li>
                        <li>Backend API work with Java and Spring Boot</li>
                        <li>Cloud infrastructure with AWS CDK and Terraform</li>
                        <li>AWS services: DynamoDB, EC2, Glue, IAM, Lambda, Redshift, RDS, S3</li>
                        <li>Pragmatic modernization for enterprise systems</li>
                    </ul>
                </div>
            </section>

            <section class="section-grid" id="experience">
                <div class="section-heading">
                    <span class="eyebrow">Experience</span>
                    <h2>Where I've worked.</h2>
                </div>

                <div class="feature-card feature-card--wide">
                    <div class="experience-item">
                        <div class="experience-item__header">
                            <h3>Solutions Architect</h3>
                            <span class="experience-item__company">Amazon Web Services</span>
                            <span class="experience-item__meta">Department of Justice · Current</span>
                        </div>
                        <p>
                            Supporting the Department of Justice as an embedded
                            AWS Solutions Architect, helping teams design and
                            adopt cloud architecture across mission-critical
                            workloads.
                        </p>
                    </div>

                    <div class="experience-item">
                        <div class="experience-item__header">
                            <h3>Software Development Engineer II</h3>
                            <span class="experience-item__company">Amazon</span>
                            <span class="experience-item__meta">June 2022 – Present</span>
                        </div>
                        <ul class="detail-list">
                            <li>Led design and implementation of a multi-tenant service that automated $45M+ worth of financial transactions with 15% YoY growth</li>
                            <li>Architected a data mesh strategy connecting 5+ systems for financial reporting, visualizing 100s of billions of dollars in transactions</li>
                            <li>Mentored a Software Engineering Apprentice through promotion to full-time Software Engineer</li>
                            <li>Coached teams through security reviews, finding and remediating dozens of vulnerabilities in financial applications</li>
                            <li>Led Operational Readiness Review (ORR) certification, creating runbooks and alarms to streamline operational work</li>
                        </ul>
                    </div>

                    <div class="experience-item">
                        <div class="experience-item__header">
                            <h3>Senior Software Engineer</h3>
                            <span class="experience-item__company">Excella Consulting</span>
                            <span class="experience-item__meta">July 2018 – June 2022 · Department of Homeland Security</span>
                        </div>
                        <ul class="detail-list">
                            <li>Architected migration from server-based to serverless container architecture, reducing manual operational work by 250 hours per year</li>
                            <li>Automated AWS infrastructure deployment via Jenkins and Terraform, reducing deployment issues by 30% and time by 20%</li>
                            <li>Created a CI/CD pipeline with Jenkins, Docker, and Cypress to prevent regression bugs</li>
                            <li>Reduced test runtime by 80% by introducing a new testing pattern</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section class="section-grid section-grid--contact" id="contact">
                <div class="section-heading">
                    <span class="eyebrow">Contact</span>
                    <h2>Let’s talk.</h2>
                </div>

                <div class="feature-card feature-card--wide">
                    <p class="contact-copy">
                        If you want to talk about software delivery, cloud
                        infrastructure, public-sector modernization, or personal
                        projects, reach out through any of the channels below.
                    </p>
                    <div class="contact-links">
                        <a
                            class="contact-link"
                            href="mailto:michaelwmason95@gmail.com"
                        >
                            <img src="./../assets/email.png" alt="Email" />
                            <span>Email</span>
                        </a>
                        <a
                            class="contact-link"
                            href="./../assets/Michael_Mason_Resume.docx"
                            download="Michael_Mason_Resume.docx"
                        >
                            <img src="./../assets/resume.png" alt="Resume" />
                            <span>Resume</span>
                        </a>
                        <a
                            class="contact-link"
                            href="https://github.com/michaelwmason"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img src="./../assets/github.png" alt="GitHub" />
                            <span>GitHub</span>
                        </a>
                        <a
                            class="contact-link"
                            href="https://www.linkedin.com/in/michael-mason-59713a90/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src="./../assets/linkedin.png"
                                alt="LinkedIn"
                            />
                            <span>LinkedIn</span>
                        </a>
                    </div>
                </div>
            </section>
        </main>
    </div>
</div>
